import BaseComponent from '../../components/base-component/base-component'
import type { Nullable } from '../../types/common'
import type VariantPicker from '../../components/product/product-variant-picker'
import type ProductModal from './product-modal'
import type Carousel from '../carousel/carousel'
import type CarouselDots from '../carousel/carousel-dots'
import { checkMobileVersion } from '../../utils/check-media'

class ProductContent extends BaseComponent {
  templateImagesAttribute: string
  templateModalAttribute: string
  imagesContainerAttribute: string
  modalSelector: string

  variantPicker: VariantPicker
  optionToTrack: string | null
  optionPosition: string | null

  templates: HTMLTemplateElement[]
  modalTemplates: HTMLTemplateElement[]
  containers: NodeListOf<Element>
  modal: Nullable<ProductModal>
  carousels: Carousel[] | CarouselDots[]

  initialView: boolean
  resizeObserver: ResizeObserver
  currentVariant: string

  constructor() {
    super()

    this.templateImagesAttribute = 'data-template-for-option'
    this.templateModalAttribute = 'data-template-for-modal-option'
    this.imagesContainerAttribute = 'data-product-media-container'
    this.modalSelector = 'product-modal'

    this.variantPicker = this.querySelector('variant-picker')
    this.optionToTrack = this.getAttribute('data-option-to-track')
    this.optionPosition = this.getAttribute('data-option-position')

    this.templates = [
      ...this.querySelectorAll(`[${this.templateImagesAttribute}]`),
    ]
    this.modalTemplates = [
      ...this.querySelectorAll(`[${this.templateModalAttribute}]`),
    ]
    this.containers = this.querySelectorAll(
      `[${this.imagesContainerAttribute}]`,
    )
    this.modal =
      this.querySelector(this.modalSelector) ??
      document.querySelector(this.modalSelector)

    this.carousels = [
      ...this.querySelectorAll('carousel-component, carousel-dots'),
    ]

    this.initialView = checkMobileVersion()

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  connectedCallback(): void {
    customElements
      .whenDefined('variant-picker')
      .then(() => {
        this.handleVariantChange({
          variant: this.variantPicker.variant,
          optionChanged: this.optionToTrack,
        })

        this.variantPicker.subscribe('variantChange', this.handleVariantChange)
        this.currentVariant = this.variantPicker.variant
      })
      .catch(error => {
        console.log(error)
      })
  }

  disconnectedCallback(): void {
    this.variantPicker.unsubscribe('variantChange', this.handleVariantChange)
  }

  handleVariantChange = ({ variant, optionChanged }): void => {
    if (optionChanged !== this.optionToTrack) {
      return
    }

    const option = variant[this.optionPosition].toLowerCase()

    this.setImages(option)
    this.variantPicker.updateFirstMediaByVariant(variant)

    if (this.modal) {
      this.setModal(option)
    }

    this.currentVariant = variant
  }

  setImages = (option): void => {
    const divsToPaste = this.templates
      .find(template => template.dataset.templateForOption === option)
      ?.content.querySelectorAll(`[${this.imagesContainerAttribute}]`)

    this.containers.forEach((container, index) => {
      const clone = divsToPaste[index].cloneNode(true)
      container.replaceWith(clone)
    })

    this.containers = this.querySelectorAll(
      `[${this.imagesContainerAttribute}]`,
    )

    this.carousels = [
      ...this.querySelectorAll('carousel-component, carousel-dots'),
    ]
  }

  setModal = (option): void => {
    const modalToPaste = this.modalTemplates.find(
      template => template.dataset.templateForModalOption === option,
    )?.content.firstElementChild

    const clone = modalToPaste?.cloneNode(true)
    this.modal?.replaceWith(clone)

    this.modal =
      this.querySelector(this.modalSelector) ??
      document.querySelector(this.modalSelector)
  }

  reInitCarousels = (): void => {
    this.carousels
      .filter(carousel => carousel.clientWidth > 0)
      .forEach(carousel => {
        carousel.reInit()
      })
  }

  handleResize = (): void => {
    const currentView = checkMobileVersion()

    if (currentView !== this.initialView) {
      this.handleVariantChange({
        variant: this.currentVariant,
        optionChanged: this.optionToTrack,
      })

      this.reInitCarousels()

      this.initialView = currentView
    }
  }
}

export default ProductContent
